import { useApiV10EmployeesBeneficiariesCreate } from '@ben/api/gen/codegen';
import {
  BeneficiaryRequestTypeEnum,
  ContactTypeEnum,
} from '@ben/api/gen/codegen.schemas';
import ActionsPopover from '@ben/orion/actions-popover';
import type { Action } from '@ben/orion/actions-popover/popover-content';
import Button from '@ben/orion/button';
import { IconName } from '@ben/orion/icon';
import InlineTextButton from '@ben/orion/inline-text-button';
import Slider from '@ben/orion/slider';
import TruncatedText from '@ben/orion/truncated-text';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import { useEmployeeId } from '@ben/shared/components/profile/dependents/use-employee-id';
import type {
  IEmployeeBenefit,
  IModularBenefitBeneficiary,
  IModularBenefitContact,
  IModularBenefitEnrolmentWindow,
} from '@ben/shared/types/modular-benefit';
import type { IValidationError } from '@ben/shared/types/validation';
import { VisuallyHidden } from '@mantine/core';
import { isNil, uniqBy } from 'lodash';
import { useEffect, useEffectEvent, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import styled, { css } from 'styled-components';
import ConditionalApprovalInfo from '../conditional-approval-info';
import ConfigWindowClosedBanner from '../config-window-closed-banner';
import type { ISliderValue } from '../multi-select-options/multi-select-options';
import SliderProgress from '../slider-progress/slider-progress';
import WindowInfo from '../window-info';
import BeneficiariesEmptyState from './beneficiaries-empty-state';
import type { AllocationError } from './beneficiary-allocation';
import {
  getAllocationError,
  getAllocationTotal,
  getPercentageCeiling,
} from './beneficiary-allocation';
import { useBeneficiaryFormTakeover } from './beneficiary-form-takeover';
import type { NomineeDetails } from './beneficiary-picker';
import {
  contactName,
  getAddableBeneficiaries,
  getAddableContacts,
  hasDuplicateNominee,
  resolveNominee,
  toPendingBeneficiaryId,
} from './beneficiary-picker';
import NomineePicker from './nominee-picker';

export interface BeneficiariesProps {
  values?: ISliderValue[];
  contacts?: IModularBenefitContact[];
  beneficiaries?: IModularBenefitBeneficiary[];
  onChange: (values: ISliderValue[]) => void;
  handleValidationErrorCallback: (validationError: IValidationError) => void;
  benefit?: IEmployeeBenefit;
  id?: string;
  benefitId?: string | number;
  title?: string;
  showTitle?: boolean;
  acceptedContactTypes?: string[];
  configWindow?: IModularBenefitEnrolmentWindow;
  isConditionalApprovalTriggered?: boolean;
  required?: boolean;
  disabled?: boolean;
  backendErrors?: Record<string, unknown>[];
}

interface Nominee extends Partial<NomineeDetails> {
  id: string;
  name: string;
  percentage: number | string;
}

const PERCENTAGE_STEP = 0.01;

const ALLOCATION_ERROR_KEYS: Record<AllocationError, string> = {
  missing: 'benefit.beneficiariesRequiredError',
  duplicate: 'benefit.beneficiariesDuplicateError',
  pending: 'benefit.beneficiariesPendingError',
  zero: 'benefit.beneficiariesZeroAllocationError',
  total: 'contacts.beneficiariesAllocationsError',
};

const Beneficiaries = ({
  values: selection,
  contacts = [],
  beneficiaries = [],
  onChange,
  handleValidationErrorCallback,
  benefit,
  acceptedContactTypes,
  configWindow,
  isConditionalApprovalTriggered,
  required,
  disabled,
}: BeneficiariesProps) => {
  // The fallback is a new array every render, so the effects below key on the
  // raw prop instead.
  const values = selection ?? [];
  const { t } = useTranslation();
  const employeeId = useEmployeeId();
  const {
    open: openBeneficiaryForm,
    savedBeneficiaries,
    saveBeneficiaryRow,
  } = useBeneficiaryFormTakeover();

  // Writes can settle outside the render cycle (mutation callbacks, the form's
  // onSaved, the slider's debounce) and would otherwise rebuild the selection
  // from a stale render; every write reads and refreshes the latest selection.
  const valuesRef = useRef(values);
  useEffect(
    function syncValuesRef() {
      valuesRef.current = selection ?? [];
    },
    [selection]
  );
  const updateValues = (next: ISliderValue[]) => {
    valuesRef.current = next;
    onChange(next);
  };

  // Rows saved on this page come first so an edited record shows its new
  // details over the payload's copy.
  const knownBeneficiaries = uniqBy(
    [...savedBeneficiaries, ...beneficiaries],
    'id'
  );

  // The same person selected twice under different id forms should never
  // happen; it is checked before the allocation so the message names it.
  const allocationError = hasDuplicateNominee(values, knownBeneficiaries)
    ? 'duplicate'
    : getAllocationError(values, Boolean(required));

  // The host hands down a new callback identity every render, so it is read
  // as an event rather than as an effect dependency.
  const reportValidation = useEffectEvent((validationError: IValidationError) =>
    handleValidationErrorCallback(validationError)
  );
  // The EA benefit hosts clear a component's validation on every change, so
  // the state is reported on every change of the selection too, not only when
  // the error itself changes.
  useEffect(
    function reportValidationToParent() {
      reportValidation(
        allocationError
          ? {
              hasError: true,
              message: [t(ALLOCATION_ERROR_KEYS[allocationError])],
            }
          : { hasError: false }
      );
    },
    [allocationError, selection, t]
  );

  // A picked dependant is selected straight away under a pending id; the
  // record is created behind it and its id swapped in (or the row dropped)
  // once the request settles.
  const { mutate: createLinkedBeneficiary } =
    useApiV10EmployeesBeneficiariesCreate({
      mutation: {
        onSuccess: function recordCreatedBeneficiary(beneficiary) {
          const { person } = beneficiary;
          const contact = contacts.find(
            ({ id }) => id === person.linkedContact
          );
          saveBeneficiaryRow({
            id: beneficiary.id,
            type: beneficiary.type,
            name: `${person.firstName} ${person.lastName}`,
            relationship: person.relationship,
            relationshipLabel: contact?.contactType ?? person.relationship,
            linkedContact: person.linkedContact,
            disabled: false,
          });
          const pendingId = toPendingBeneficiaryId(person.linkedContact);
          updateValues(
            valuesRef.current.map((value) =>
              value.id === pendingId ? { ...value, id: beneficiary.id } : value
            )
          );
        },
        onError: function dropPendingNominee(_error, { data }) {
          // Always sent as a number by handleQuickAdd; the request type is wider.
          const pendingId = toPendingBeneficiaryId(
            Number(data.person.linkedContact)
          );
          updateValues(
            valuesRef.current.filter((value) => value.id !== pendingId)
          );
          toast.error(t('errors.serverError'));
        },
      },
    });

  // The first nominee takes the whole allocation; later ones start at 0 and
  // are redistributed by the user.
  const addNominee = (id: string) => {
    const current = valuesRef.current;
    updateValues([
      ...current,
      { id, percentage: current.length === 0 ? 100 : 0 },
    ]);
  };

  // Re-creating an already-nominated person returns the same record, and an
  // edit keeps its nomination: the selection only grows for a new record.
  const handleSaved = (beneficiary: IModularBenefitBeneficiary) => {
    if (valuesRef.current.some(({ id }) => id === beneficiary.id)) {
      return;
    }
    addNominee(beneficiary.id);
  };

  const handleAddClick = () =>
    openBeneficiaryForm({ acceptedContactTypes, onSaved: handleSaved });

  const handleEditClick = (beneficiaryId: string) =>
    openBeneficiaryForm({
      acceptedContactTypes,
      beneficiaryId,
      onSaved: handleSaved,
    });

  const handleQuickAdd = (contactId: number) => {
    addNominee(toPendingBeneficiaryId(contactId));
    createLinkedBeneficiary({
      employeeId,
      data: {
        type: BeneficiaryRequestTypeEnum.PERSON,
        person: { linkedContact: contactId },
      },
    });
  };

  const handlePercentageChange = (id: string, percentage: number) =>
    updateValues(
      valuesRef.current.map((value) =>
        value.id === id ? { ...value, percentage } : value
      )
    );

  // Removing only drops the nomination; the beneficiary record is kept.
  const handleRemove = (id: string) =>
    updateValues(valuesRef.current.filter((value) => value.id !== id));

  const getRowActions = (id: string): Action[] => {
    const record = knownBeneficiaries.find((row) => row.id === id);
    return [
      {
        label: t('benefit.beneficiariesEditDetails'),
        handleClick: () => handleEditClick(id),
        // Only a record can be edited here, and never the employee's own.
        shouldShow:
          !isNil(record) && record.relationship !== ContactTypeEnum.SELF,
      },
      {
        label: t('words.remove'),
        handleClick: () => handleRemove(id),
        shouldShow: true,
      },
    ];
  };

  const addableContacts = getAddableContacts(
    contacts,
    knownBeneficiaries,
    values
  );
  const addableBeneficiaries = getAddableBeneficiaries(
    knownBeneficiaries,
    values
  );
  const hasPickerRows =
    addableContacts.length > 0 || addableBeneficiaries.length > 0;

  // Outside the enrolment window nobody new can be nominated; the existing
  // allocation stays editable unless the host disables the whole section.
  const canAmendSelection =
    configWindow?.isOpen ||
    benefit?.enrolment?.configWindowOpen ||
    !benefit?.enrolment?.lockedWindowOpen;
  const isAddDisabled = disabled || !canAmendSelection;

  const notices = (
    <>
      {!canAmendSelection && <ConfigWindowClosedBanner />}
      {!isNil(configWindow) && (
        <WindowInfo
          startDate={configWindow.startTimestamp}
          endDate={configWindow.endTimestamp}
        />
      )}
      {isConditionalApprovalTriggered && <ConditionalApprovalInfo />}
    </>
  );

  const picker = hasPickerRows && (
    <>
      {addableContacts.length > 0 && (
        <NomineePicker
          label={t('benefit.beneficiariesDependantsLabel')}
          rows={addableContacts.map((contact) => ({
            id: String(contact.id),
            name: contactName(contact),
            relationshipLabel: contact.contactType,
          }))}
          disabled={isAddDisabled}
          onAdd={(id) => handleQuickAdd(Number(id))}
        />
      )}
      {addableBeneficiaries.length > 0 && (
        <NomineePicker
          label={t('benefit.beneficiariesLabel')}
          rows={addableBeneficiaries}
          disabled={isAddDisabled}
          onAdd={addNominee}
        />
      )}
      <Divider />
    </>
  );

  if (values.length === 0) {
    return (
      <Container>
        {notices}
        <BeneficiariesEmptyState
          hasPickerRows={hasPickerRows}
          disabled={isAddDisabled}
          onAddClick={handleAddClick}
        >
          {picker}
        </BeneficiariesEmptyState>
      </Container>
    );
  }

  // An entry that matches neither list still gets a row, so its share stays
  // visible and it can be removed.
  const selected = values.map(
    (value): Nominee => ({
      id: value.id,
      percentage: value.percentage,
      ...(resolveNominee(value.id, contacts, knownBeneficiaries) ?? {
        name: t('benefit.beneficiariesUnknownName'),
      }),
    })
  );

  return (
    <Container>
      {notices}
      <SliderProgress
        totalPercentage={getAllocationTotal(values)}
        sliderValues={values}
        disabled={Boolean(disabled)}
      />
      <SelectedCard>
        {selected.map((beneficiary) => (
          <SelectedRow key={beneficiary.id}>
            <AllocationRow>
              <NameBlock>
                <TruncatedText
                  as={TypographyVariant.h3}
                  variant={TypographyVariant.h3}
                >
                  {beneficiary.name}
                </TruncatedText>
                {beneficiary.relationshipLabel && (
                  <RelationshipLabel variant={TypographyVariant.bodyLg2}>
                    {beneficiary.relationshipLabel}
                  </RelationshipLabel>
                )}
                {beneficiary.disabled && (
                  <NotEligibleLabel variant={TypographyVariant.bodyMd1}>
                    {t('benefit.beneficiariesNotEligible')}
                  </NotEligibleLabel>
                )}
              </NameBlock>
              <SliderWrapper>
                <VisuallyHidden
                  renderRoot={(props) => (
                    <label
                      {...props}
                      htmlFor={`beneficiary-percentage-${beneficiary.id}`}
                    />
                  )}
                >
                  {beneficiary.name}
                </VisuallyHidden>
                <Slider
                  inputId={`beneficiary-percentage-${beneficiary.id}`}
                  showLabel={false}
                  label={beneficiary.name}
                  isPercentage
                  step={PERCENTAGE_STEP}
                  max={getPercentageCeiling(values, beneficiary.id)}
                  value={Number(beneficiary.percentage)}
                  disabled={disabled}
                  onChange={(percentage) =>
                    handlePercentageChange(beneficiary.id, percentage)
                  }
                />
              </SliderWrapper>
            </AllocationRow>
            <RowActions>
              <ActionsPopover
                closeOnActionClick
                actions={getRowActions(beneficiary.id)}
                trigger={(onClick) => (
                  <InlineTextButton
                    label={t('words.edit')}
                    iconName={IconName.Edit}
                    isLeftIcon
                    disabled={disabled}
                    onClick={onClick}
                  />
                )}
              />
            </RowActions>
          </SelectedRow>
        ))}
        {picker}
        <Button
          type="button"
          variant="primary"
          disabled={isAddDisabled}
          onClick={handleAddClick}
        >
          {t('benefit.beneficiariesAddAnotherButton')}
        </Button>
      </SelectedCard>
    </Container>
  );
};

export default Beneficiaries;

const Container = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(4)};
  `}
`;

const SelectedCard = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${theme.spacing(2)};
    padding: ${theme.spacing(3.75)};
    background-color: ${theme.palette.neutral[40]};
    border: 1px solid ${theme.palette.neutral[60]};
    border-radius: ${theme.borderRadius.sm};
  `}
`;

const SelectedRow = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2.25)};
    width: 100%;
    padding-bottom: ${theme.spacing(2)};
    border-bottom: 1px solid ${theme.palette.neutral[60]};
  `}
`;

const AllocationRow = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${theme.spacing(1.75)};
  `}
`;

// Shrinks below its content so a long name truncates instead of pushing the
// slider out of the card.
const NameBlock = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

// The 128px track from the design plus the slider's own input and % sign.
const SliderWrapper = styled.div`
  flex: 0 0 232px;
`;

const RowActions = styled.div`
  ${({ theme }) => css`
    display: flex;
    justify-content: flex-end;
    padding-right: ${theme.spacing(2.5)};
  `}
`;

const RelationshipLabel = styled(Typography)`
  ${({ theme }) => css`
    color: ${theme.palette.neutral[80]};
  `}
`;

const NotEligibleLabel = styled(Typography)`
  ${({ theme }) => css`
    color: ${theme.palette.warning.main};
  `}
`;

const Divider = styled.hr`
  ${({ theme }) => css`
    width: 100%;
    margin: ${theme.spacing(1, 0, 0)};
    border: 0;
    border-top: 1px solid ${theme.palette.neutral[60]};
  `}
`;
